import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { sk } from "date-fns/locale";
import { CalendarIcon, Loader2, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { getLocalizedInvoiceDetailUrl, getLocalizedUrl } from "@/lib/localized-routes";
import { cn } from "@/lib/utils";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

export type BillingMode = "fixed" | "hourly" | "items";

export interface QuickTaskItem {
  name: string;
  quantity: number;
  unitPrice: number;
  vatRate?: number;
  amount: number;
}

interface Client {
  id: number;
  name: string;
  email?: string;
  invoiceEmail?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  hourlyRate?: string | number;
  dueDays?: number;
}

export type QuickTaskInitialAction = 'save' | 'invoice' | 'invoice_and_send';

interface QuickTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clients: Client[];
  defaultClientId?: number | null;
  defaultBillingMode?: BillingMode;
  onCreateSuccess?: () => void;
  /** When set, shows only the specified action as primary (e.g. from onboarding step 3) */
  initialAction?: QuickTaskInitialAction;
}

function checkBillingMinimum(client: Client & Record<string, unknown>): { ok: boolean; missing: string[] } {
  const missing: string[] = [];
  const email = client.invoiceEmail || client.email || (client as any).invoice_email;
  if (!email || String(email).trim() === "") missing.push("email");
  const street = client.street || (client as any).street;
  if (!street || String(street).trim() === "") missing.push("street");
  const city = client.city || (client as any).city;
  if (!city || String(city).trim() === "") missing.push("city");
  const postalCode = client.postalCode || (client as any).postal_code;
  if (!postalCode || String(postalCode).trim() === "") missing.push("postalCode");
  const country = client.country || (client as any).country;
  if (!country || String(country).trim() === "") missing.push("country");
  return { ok: missing.length === 0, missing };
}

/** Robust parsing of invoice ID from POST /api/invoices response (supports id, invoiceId, invoice_id) */
function parseInvoiceIdFromResponse(data: unknown): number | null {
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  const raw = obj.id ?? obj.invoiceId ?? (obj as any).invoice_id;
  if (raw === undefined || raw === null) return null;
  const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : null;
}

export function QuickTaskModal({
  open,
  onOpenChange,
  clients,
  defaultClientId,
  defaultBillingMode = "fixed",
  onCreateSuccess,
  initialAction,
}: QuickTaskModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState<string>(defaultClientId ? String(defaultClientId) : "");
  const [dueDate, setDueDate] = useState<Date>(new Date());
  const [billingMode, setBillingMode] = useState<BillingMode>(defaultBillingMode);

  // Sync defaults when modal opens (e.g. from onboarding with preselected client)
  useEffect(() => {
    if (open) {
      if (defaultClientId != null) setClientId(String(defaultClientId));
      if (defaultBillingMode) setBillingMode(defaultBillingMode);
    }
  }, [open, defaultClientId, defaultBillingMode]);

  // Mode A: fixed amount
  const [fixedAmount, setFixedAmount] = useState("");

  // Mode B: hourly
  const [hours, setHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");

  // Mode C: items
  const [items, setItems] = useState<QuickTaskItem[]>([
    { name: "", quantity: 1, unitPrice: 0, amount: 0 },
  ]);

  // Billing data modal
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [pendingInvoiceTaskId, setPendingInvoiceTaskId] = useState<number | null>(null);
  const [pendingInvoiceAndSend, setPendingInvoiceAndSend] = useState<boolean>(false);
  const [failedInvoiceTaskId, setFailedInvoiceTaskId] = useState<number | null>(null);
  const [invoiceIdNullState, setInvoiceIdNullState] = useState<boolean>(false);
  const [progressStep, setProgressStep] = useState<'idle' | 'saving_task' | 'creating_invoice' | 'sending_email'>('idle');
  const [billingData, setBillingData] = useState({
    email: "",
    street: "",
    city: "",
    postalCode: "",
    country: "Slovensko",
  });

  const selectedClient = clients.find((c) => c.id.toString() === clientId);

  // Set default hourly rate from client when client changes
  useEffect(() => {
    const rate = selectedClient?.hourlyRate ?? (selectedClient as any)?.hourly_rate;
    if (rate !== undefined && rate !== null) {
      setHourlyRate(String(rate));
    }
  }, [selectedClient?.id, selectedClient?.hourlyRate, (selectedClient as any)?.hourly_rate]);

  const createTaskMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/tasks", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["/api/tasks"] });
      queryClient.removeQueries({ queryKey: ["/api/tasks/statistics"] });
      queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
      queryClient.refetchQueries({ queryKey: ["/api/tasks/statistics"] });
    },
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async (data: { invoiceData: any; taskIds: number[] }) => {
      const res = await apiRequest("POST", "/api/invoices", data);
      return res.json();
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/clients/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    },
  });

  const buildTaskPayload = () => {
    const base = {
      title: title.trim() || "Nová úloha",
      description: "",
      clientId: clientId ? parseInt(clientId) : null,
      status: "pending",
      taskType: "standard" as const,
      dueDate: dueDate?.toISOString?.() ?? new Date().toISOString(),
      isStarred: false,
      isInvoiced: false,
      items: [] as { name: string; quantity: number; unitPrice: number; amount: number }[],
    };

    if (billingMode === "fixed") {
      const amount = parseFloat(fixedAmount) || 0;
      base.taskType = "cash";
      base.items = [{ name: base.title, quantity: 1, unitPrice: amount, amount }];
      (base as any).manualAmount = amount;
      (base as any).totalAmount = amount;
    } else if (billingMode === "hourly") {
      const h = parseFloat(hours) || 0;
      const rate = parseFloat(hourlyRate) || 0;
      base.items = [{ name: base.title, quantity: h, unitPrice: rate, amount: h * rate }];
      (base as any).hoursWorked = h;
      (base as any).hourlyRate = rate;
      (base as any).actual_hours = h;
    } else {
      base.items = items
        .filter((i) => (i.name?.trim() || "").length > 0 && (i.quantity || 0) > 0 && (i.unitPrice || 0) >= 0)
        .map((i) => ({
          name: i.name.trim(),
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          amount: i.quantity * i.unitPrice,
        }));
    }

    return base;
  };

  const validate = (): string | null => {
    if (!title?.trim()) return "Názov úlohy je povinný";
    if (!clientId) return "Vyberte klienta";
    if (billingMode === "fixed" && (parseFloat(fixedAmount) || 0) <= 0) return "Zadajte sumu";
    if (billingMode === "hourly") {
      if ((parseFloat(hours) || 0) <= 0) return "Zadajte hodiny";
      if ((parseFloat(hourlyRate) || 0) < 0) return "Zadajte hodinovú sadzbu";
    }
    if (billingMode === "items") {
      const valid = items.filter((i) => (i.name?.trim() || "").length > 0 && (i.quantity || 0) > 0 && (i.unitPrice || 0) >= 0);
      if (valid.length === 0) return "Pridajte aspoň jednu položku s názvom, množstvom a cenou";
    }
    return null;
  };

  const handleSave = async (action: 'save' | 'invoice' | 'invoice_and_send') => {
    const err = validate();
    if (err) {
      toast({ title: "Chyba", description: err, variant: "destructive" });
      return;
    }

    const payload = buildTaskPayload();

    try {
      setProgressStep('saving_task');
      const newTask = await createTaskMutation.mutateAsync(payload);

      if ((action === 'invoice' || action === 'invoice_and_send') && selectedClient) {
        const billing = checkBillingMinimum(selectedClient);
        if (!billing.ok) {
          setPendingInvoiceTaskId(newTask.id);
          setPendingInvoiceAndSend(action === 'invoice_and_send');
          const c = selectedClient as any;
          setBillingData({
            email: c.invoiceEmail || c.email || c.invoice_email || "",
            street: c.street || "",
            city: c.city || "",
            postalCode: c.postalCode || c.postal_code || "",
            country: c.country || "Slovensko",
          });
          setShowBillingModal(true);
          setProgressStep('idle');
          return;
        }
        setProgressStep('idle');
        if (action === 'invoice_and_send') {
          await doCreateInvoiceAndSend(newTask.id);
        } else {
          await doCreateInvoice(newTask.id);
        }
      } else {
        setProgressStep('idle');
        onOpenChange(false);
        onCreateSuccess?.();
        toast({ title: "Úloha uložená", description: "Úloha bola úspešne vytvorená." });
      }
    } catch (e: any) {
      setProgressStep('idle');
      toast({ title: "Chyba", description: e?.message || "Nepodarilo sa uložiť úlohu", variant: "destructive" });
    }
  };

  const doCreateInvoice = (taskId: number, andSend = false, overrideEmail?: string) => {
    const client = clients.find((c) => c.id.toString() === clientId) as any;
    if (!client) return;

    const today = new Date();
    const due = new Date();
    due.setDate(due.getDate() + (client.dueDays ?? client.due_days ?? 14));

    const invoiceData = {
      clientId: client.id,
      status: "draft",
      issueDate: today.toISOString(),
      dueDate: due.toISOString(),
      currency: "EUR",
    };

    setFailedInvoiceTaskId(null);
    setInvoiceIdNullState(false);
    createInvoiceMutation.mutate(
      { invoiceData, taskIds: [taskId] },
      {
        onSuccess: async (invoice) => {
          const invoiceId = parseInvoiceIdFromResponse(invoice);
          queryClient.removeQueries({ queryKey: ["/api/invoices"] });
          queryClient.removeQueries({ queryKey: ["/api/tasks"] });
          queryClient.refetchQueries({ queryKey: ["/api/invoices"] });
          queryClient.refetchQueries({ queryKey: ["/api/tasks"] });
          setPendingInvoiceTaskId(null);
          setShowBillingModal(false);

          if (invoiceId != null) {
            if (andSend) {
              setProgressStep('sending_email');
              const toEmail = overrideEmail || client.invoiceEmail || client.email || (client as any).invoice_email;
              try {
                await apiRequest('POST', `/api/invoices/${invoiceId}/status`, { status: 'sent', email: toEmail });
                queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
                setProgressStep('idle');
                onOpenChange(false);
                setLocation(getLocalizedInvoiceDetailUrl(invoiceId));
                toast({ title: "Faktúra odoslaná emailom", description: "Faktúra bola úspešne odoslaná na email." });
              } catch (sendErr: any) {
                setProgressStep('idle');
                onOpenChange(false);
                setLocation(getLocalizedInvoiceDetailUrl(invoiceId) + '?sendFailed=1');
                toast({
                  title: "Faktúra vytvorená",
                  description: "Faktúra bola vytvorená, ale nepodarilo sa odoslať email. Skúste odoslať znova z detailu faktúry.",
                  variant: "destructive",
                });
              }
            } else {
              setProgressStep('idle');
              onOpenChange(false);
              setLocation(getLocalizedInvoiceDetailUrl(invoiceId));
              toast({ title: "Faktúra úspešne vytvorená", description: "Faktúra bola úspešne vytvorená." });
            }
          } else {
            setProgressStep('idle');
            setInvoiceIdNullState(true);
            toast({
              title: "Faktúra bola vytvorená",
              description: "Faktúra bola vytvorená, ale nepodarilo sa otvoriť detail.",
              variant: "default",
            });
          }
        },
        onError: (error: Error) => {
          setProgressStep('idle');
          setFailedInvoiceTaskId(taskId);
          toast({
            title: "Úloha uložená",
            description: `Faktúra sa nepodarila vytvoriť: ${error.message}`,
            variant: "destructive",
          });
        },
      }
    );
  };

  const doCreateInvoiceAndSend = (taskId: number, overrideEmail?: string) => {
    setProgressStep('creating_invoice');
    doCreateInvoice(taskId, true, overrideEmail);
  };

  const handleBillingSave = async () => {
    if (!selectedClient || !pendingInvoiceTaskId) return;
    const email = billingData.email?.trim();
    const street = billingData.street?.trim();
    const city = billingData.city?.trim();
    const postalCode = billingData.postalCode?.trim();
    const country = billingData.country?.trim();
    if (!email || !street || !city || !postalCode || !country) {
      toast({ title: "Chyba", description: "Vyplňte všetky povinné polia", variant: "destructive" });
      return;
    }

    try {
      await updateClientMutation.mutateAsync({
        id: selectedClient.id,
        data: {
          invoiceEmail: email,
          street,
          city,
          postalCode,
          country,
        },
      });
      if (pendingInvoiceAndSend) {
        doCreateInvoiceAndSend(pendingInvoiceTaskId, email);
      } else {
        doCreateInvoice(pendingInvoiceTaskId);
      }
      setPendingInvoiceAndSend(false);
    } catch (e: any) {
      toast({ title: "Chyba", description: e?.message || "Nepodarilo sa uložiť údaje", variant: "destructive" });
    }
  };

  const subtotal = billingMode === "items"
    ? items.reduce((s, i) => s + (i.quantity || 0) * (i.unitPrice || 0), 0)
    : billingMode === "fixed"
      ? parseFloat(fixedAmount) || 0
      : (parseFloat(hours) || 0) * (parseFloat(hourlyRate) || 0);

  const isVatPayer = (user as any)?.isVatPayer === true;
  const vatRate = parseFloat(String((user as any)?.companyVatRate ?? 20)) || 20;
  const vatAmount = isVatPayer ? subtotal * (vatRate / 100) : 0;
  const totalWithVat = subtotal + vatAmount;

  const firstItemNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && billingMode === "items") {
      setTimeout(() => firstItemNameRef.current?.focus(), 100);
    }
  }, [open, billingMode]);

  const addItem = () => setItems((prev) => [...prev, { name: "", quantity: 1, unitPrice: 0, amount: 0 }]);
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const updateItem = (idx: number, field: keyof QuickTaskItem, value: any) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === "quantity" || field === "unitPrice") {
        next[idx].amount = (next[idx].quantity || 0) * (next[idx].unitPrice || 0);
      }
      return next;
    });
  };

  const isItemRowValid = (item: QuickTaskItem) =>
    (item.name?.trim() || "").length > 0 && (item.quantity || 0) > 0 && (item.unitPrice || 0) >= 0;

  const handleItemUnitPriceKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const item = items[idx];
      if (isItemRowValid(item)) {
        e.preventDefault();
        addItem();
      }
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFailedInvoiceTaskId(null);
      setInvoiceIdNullState(false);
      setPendingInvoiceAndSend(false);
    }
    onOpenChange(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="w-[95vw] sm:max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl max-h-[85vh] overflow-y-auto overflow-x-hidden min-w-0">
          <DialogHeader>
            <DialogTitle>Rýchla úloha</DialogTitle>
            <DialogDescription>Vytvorte úlohu s jednoduchým účtovaním</DialogDescription>
          </DialogHeader>

          {progressStep !== 'idle' && (
            <div className="rounded-lg border bg-muted/50 p-3 text-sm">
              {progressStep === 'saving_task' && "Ukladám úlohu…"}
              {progressStep === 'creating_invoice' && "Vytváram faktúru…"}
              {progressStep === 'sending_email' && "Odosielam email…"}
            </div>
          )}

          <div className="space-y-4 min-w-0">
            <div>
              <Label>Názov úlohy *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Názov úlohy" />
            </div>

            <div>
              <Label>Klient *</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Vyberte klienta" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Termín</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "d. M. yyyy", { locale: sk }) : "Vyberte dátum"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={dueDate} onSelect={(d) => d && setDueDate(d)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="mb-2 block">Spôsob účtovania</Label>
              <Select value={billingMode} onValueChange={(v) => setBillingMode(v as BillingMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Pevná suma</SelectItem>
                  <SelectItem value="hourly">Hodinová práca</SelectItem>
                  <SelectItem value="items">Položky</SelectItem>
                </SelectContent>
              </Select>

              {billingMode === "fixed" && (
                <div className="mt-3">
                  <Label>Suma EUR *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={fixedAmount}
                    onChange={(e) => setFixedAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              )}

              {billingMode === "hourly" && (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label>Hodiny *</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Hodinová sadzba EUR *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      placeholder={selectedClient?.hourlyRate ? String(selectedClient.hourlyRate) : "0.00"}
                    />
                  </div>
                </div>
              )}

              {billingMode === "items" && (
                <div className="mt-3 space-y-2 min-w-0">
                  <div className="flex justify-between items-center">
                    <Label>Položky</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="h-4 w-4 mr-1" /> Pridať položku
                    </Button>
                  </div>
                  <div className="w-full min-w-0 overflow-x-auto overflow-y-auto max-h-48 border rounded-md">
                    <Table className="min-w-[760px] w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Názov</TableHead>
                          <TableHead className="w-20">Množstvo</TableHead>
                          <TableHead className="w-24">Jedn. cena</TableHead>
                          <TableHead className="w-20">DPH %</TableHead>
                          <TableHead className="w-20 text-right">Suma</TableHead>
                          <TableHead className="w-10" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Input
                                ref={idx === 0 ? firstItemNameRef : undefined}
                                value={item.name}
                                onChange={(e) => updateItem(idx, "name", e.target.value)}
                                placeholder="Názov"
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.quantity || ""}
                                onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                                className="h-8 w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.unitPrice || ""}
                                onChange={(e) => updateItem(idx, "unitPrice", parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => handleItemUnitPriceKeyDown(idx, e)}
                                className="h-8 w-24"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.vatRate ?? ""}
                                onChange={(e) => updateItem(idx, "vatRate", parseFloat(e.target.value) || undefined)}
                                placeholder="-"
                                className="h-8 w-20"
                              />
                            </TableCell>
                            <TableCell className="text-right text-sm">{(item.quantity || 0) * (item.unitPrice || 0)} €</TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} disabled={items.length <= 1}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            {/* Výrazný súčet */}
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-4 pb-4">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Spolu</div>
                <div className={cn(
                  "font-bold tabular-nums",
                  (subtotal > 0 || totalWithVat > 0) ? "text-2xl sm:text-3xl text-primary" : "text-xl text-muted-foreground"
                )}>
                  {(isVatPayer ? totalWithVat : subtotal).toFixed(2)} €
                </div>
                {isVatPayer && subtotal > 0 && (
                  <div className="mt-2 text-sm text-muted-foreground space-y-0.5">
                    <div>Bez DPH: {subtotal.toFixed(2)} €</div>
                    <div>DPH ({vatRate}%): {vatAmount.toFixed(2)} €</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {failedInvoiceTaskId != null && (
              <Alert variant="destructive" className="mt-4">
                <AlertTitle>Chyba pri vytváraní faktúry</AlertTitle>
                <AlertDescription>
                  Faktúru sa nepodarilo vytvoriť. Úloha bola uložená.
                </AlertDescription>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => doCreateInvoice(failedInvoiceTaskId)}
                    disabled={createInvoiceMutation.isPending}
                  >
                    {createInvoiceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Skúsiť znova"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLocation(getLocalizedUrl("/tasks"));
                      onOpenChange(false);
                      setFailedInvoiceTaskId(null);
                    }}
                  >
                    Prejsť na úlohy
                  </Button>
                </div>
              </Alert>
            )}

            {invoiceIdNullState && (
              <Alert className="mt-4 border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                <AlertTitle>Faktúra vytvorená</AlertTitle>
                <AlertDescription>
                  Faktúra bola vytvorená, ale nepodarilo sa otvoriť detail.
                </AlertDescription>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLocation(getLocalizedUrl("/invoices"));
                      onOpenChange(false);
                      setInvoiceIdNullState(false);
                    }}
                  >
                    Otvoriť faktúry
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      setInvoiceIdNullState(false);
                    }}
                  >
                    Zatvoriť
                  </Button>
                </div>
              </Alert>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={progressStep !== 'idle'}>
              Zrušiť
            </Button>
            <div className="flex flex-wrap gap-2">
              {initialAction ? (
                /* Onboarding: zobraziť len jeden button podľa initialAction */
                <>
                  {initialAction === 'save' && (
                    <Button onClick={() => handleSave('save')} disabled={progressStep !== 'idle'}>
                      {progressStep === 'saving_task' ? <Loader2 className="h-4 w-4 animate-spin" /> : "Len uložiť úlohu"}
                    </Button>
                  )}
                  {initialAction === 'invoice' && (
                    <Button onClick={() => handleSave('invoice')} disabled={progressStep !== 'idle'}>
                      {progressStep !== 'idle' ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť a vytvoriť faktúru"}
                    </Button>
                  )}
                  {initialAction === 'invoice_and_send' && (
                    <Button onClick={() => handleSave('invoice_and_send')} disabled={progressStep !== 'idle'}>
                      {progressStep !== 'idle' ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť a odoslať faktúru"}
                    </Button>
                  )}
                </>
              ) : (
                /* Default: primary = invoice_and_send, secondary = save, invoice */
                <>
                  <Button variant="outline" size="sm" onClick={() => handleSave('save')} disabled={progressStep !== 'idle'}>
                    {progressStep === 'saving_task' ? <Loader2 className="h-4 w-4 animate-spin" /> : "Len uložiť úlohu"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleSave('invoice')} disabled={progressStep !== 'idle'}>
                    {progressStep !== 'idle' ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť a vytvoriť faktúru"}
                  </Button>
                  <div className="flex flex-col items-center gap-0.5">
                    <Button onClick={() => handleSave('invoice_and_send')} disabled={progressStep !== 'idle'}>
                      {progressStep !== 'idle' ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť a odoslať faktúru"}
                    </Button>
                    <span className="text-[10px] text-muted-foreground">Odporúčané</span>
                  </div>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing data modal */}
      <Dialog open={showBillingModal} onOpenChange={setShowBillingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Doplniť fakturačné údaje</DialogTitle>
            <DialogDescription>
              Pred vytvorením faktúry je potrebné doplniť fakturačné údaje klienta: email, adresa (ulica, mesto, PSČ, krajina).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input value={billingData.email} onChange={(e) => setBillingData((p) => ({ ...p, email: e.target.value }))} placeholder="email@example.com" />
            </div>
            <div>
              <Label>Ulica *</Label>
              <Input value={billingData.street} onChange={(e) => setBillingData((p) => ({ ...p, street: e.target.value }))} placeholder="Ulica 123" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Mesto *</Label>
                <Input value={billingData.city} onChange={(e) => setBillingData((p) => ({ ...p, city: e.target.value }))} placeholder="Bratislava" />
              </div>
              <div>
                <Label>PSČ *</Label>
                <Input value={billingData.postalCode} onChange={(e) => setBillingData((p) => ({ ...p, postalCode: e.target.value }))} placeholder="84105" />
              </div>
            </div>
            <div>
              <Label>Krajina *</Label>
              <Input value={billingData.country} onChange={(e) => setBillingData((p) => ({ ...p, country: e.target.value }))} placeholder="Slovensko" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBillingModal(false); setPendingInvoiceTaskId(null); }}>
              Zrušiť
            </Button>
            <Button onClick={handleBillingSave} disabled={updateClientMutation.isPending}>
              {updateClientMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť a pokračovať"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
